import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { Service } from '@/modules/Service/types'
import {
    createDowntimeLog,
    updateDowntimeLog,
    getServiceDowntimeLogs,
} from '@/modules/Service/services/sla'
import { SLADowntimeLog } from '@/modules/Service/types/sla'
import { updateTicket } from '../../services'
import {
    TicketAttachment,
    TicketType as TicketTypeResponse,
} from '../../types/response'
import useUploadFile from '../../types/uploadfile'
import { TICKET_ATTACHMENT_FILE_PREFIX } from '../../types/utils'
import { useTicketTemplate } from '../../templates'
import { UpdateTicketSchema } from '../Update/schema'
import { Ticket } from '../../types/response'
import {
    CreateTicketFormValues,
    EditorValue,
    SLAInfo,
    ServiceSLAData,
} from '../Create/types'
import { formatPGDate } from '@/utils/function'

dayjs.extend(utc)
dayjs.extend(timezone)

interface UseEditTicketFormProps {
    ticket: Ticket | undefined
    onSuccess?: () => void
    onCancel?: () => void
}

export const useEditTicketForm = ({
    ticket,
    onSuccess,
    onCancel,
}: UseEditTicketFormProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    // Track SLA info for display
    const [slaInfo, setSlaInfo] = useState<SLAInfo>({})

    // Track service SLA for time calculation
    const [_serviceSLA, setServiceSLA] = useState<ServiceSLAData>({})

    const { refetchAll } = useDataGridRefetch({
        reactQueryKey: 'ticket-attachment',
    })
    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticket?.ticket_id}`,
    })

    const refetchAllTicketQueries = () => {
        const queries = queryClient
            .getQueryCache()
            .getAll()
            .filter((q) => {
                const key = q.queryKey?.[0]
                return typeof key === 'string' && key.startsWith('ticket-')
            })
        queries.forEach((q) => q.fetch())
    }

    const formControl = useForm<CreateTicketFormValues>({
        defaultValues: {
            ticket_type_key: ticket?.ticket_type_key || '',
            ticket_type_group: ticket?.ticket_type_group || '',
            ticket_type_id: ticket?.ticket_type_id || -1,
            customer_id: ticket?.customer_id || -1,
            service_id: ticket?.service_id || -1,
            ticket_priority: ticket?.ticket_priority || '',
            ticket_content: ticket?.ticket_content || '',
            ticket_affected: ticket?.ticket_affected || '',
            ticket_requested_at: ticket?.ticket_requested_at
                ? dayjs.utc(ticket.ticket_requested_at).tz()
                : dayjs(),
            ticket_status: ticket?.ticket_status || '',
            ticket_technical_assignees:
                ticket?.ticket_technical_assignees || [],
            ticket_estimated_completion_date:
                ticket?.ticket_estimated_completion_date
                    ? dayjs.utc(ticket.ticket_estimated_completion_date).tz()
                    : dayjs(),
            ticket_meeting_via: ticket?.ticket_meeting_via || '',
            ticket_id: ticket?.ticket_id || -1,
            service_startdate: ticket?.service_startdate
                ? formatPGDate(String(ticket.service_startdate))
                : null,
            service_duedate: ticket?.service_duedate
                ? formatPGDate(String(ticket.service_duedate))
                : null,
            ticket_meeting_type: ticket?.ticket_meeting_type || '',
            ticket_time_repply: null,
            ticket_time_release: ticket?.ticket_time_release
                ? dayjs.utc(ticket.ticket_time_release).tz()
                : null,
            ticket_marjorable: Boolean(ticket?.ticket_marjorable) || false,
            // Downtime fields (auto-detected from existing logs)
            include_downtime: false,
            downtime_start: null,
            downtime_end: null,
            downtime_cause: 'provider',
            downtime_counts_against_sla: true,
        },
        // @ts-ignore
        resolver: yupResolver(UpdateTicketSchema(t)),
    })

    const { control, handleSubmit, watch, setValue, reset } = formControl

    // Reset form when ticket data is loaded
    useEffect(() => {
        if (ticket) {
            reset({
                ticket_type_key: ticket.ticket_type_key || '',
                ticket_type_group: ticket.ticket_type_group || '',
                ticket_type_id: ticket.ticket_type_id || -1,
                customer_id: ticket.customer_id || -1,
                service_id: ticket.service_id || -1,
                ticket_priority: ticket.ticket_priority || '',
                ticket_content: ticket.ticket_content || '',
                ticket_affected: ticket.ticket_affected || '',
                ticket_requested_at: ticket.ticket_requested_at
                    ? dayjs.utc(ticket.ticket_requested_at).tz()
                    : dayjs(),
                ticket_status: ticket.ticket_status || '',
                ticket_technical_assignees:
                    ticket.ticket_technical_assignees || [],
                ticket_estimated_completion_date:
                    ticket.ticket_estimated_completion_date
                        ? dayjs
                              .utc(ticket.ticket_estimated_completion_date)
                              .tz()
                        : dayjs(),
                ticket_meeting_via: ticket.ticket_meeting_via || '',
                ticket_id: ticket.ticket_id || -1,
                service_startdate: ticket.service_startdate
                    ? formatPGDate(String(ticket.service_startdate))
                    : null,
                service_duedate: ticket.service_duedate
                    ? formatPGDate(String(ticket.service_duedate))
                    : null,
                ticket_meeting_type: ticket.ticket_meeting_type || '',
                ticket_time_repply: null,
                ticket_time_release: ticket.ticket_time_release
                    ? dayjs.utc(ticket.ticket_time_release).tz()
                    : null,
                ticket_marjorable: Boolean(ticket.ticket_marjorable) || false,
                // Downtime fields (auto-detected from existing logs)
                include_downtime: false,
                downtime_start: null,
                downtime_end: null,
                downtime_cause: 'provider',
                downtime_counts_against_sla: true,
            })
        }
    }, [ticket, reset])

    // Template system (reserved for future use in edit mode)
    const { getDefaults: _getDefaults, getSampleContent: _getSampleContent } =
        useTicketTemplate(
            watch('ticket_type_group') || '',
            watch('ticket_type_key')
        )

    const { uploadAsync } = useUploadFile({
        prefix: TICKET_ATTACHMENT_FILE_PREFIX,
        onError: () => toast.error(t('upload-file-failed')),
    })

    const { mutateAsync: updateTicketMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: updateTicket },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('updated-success'),
                onSuccess: () => {
                    refetchAllTicketQueries()
                    refetchAll()
                    refetchLogs()
                    onSuccess?.()
                },
            }
        )

    // Extract text helper
    const extractText = (field: unknown): string => {
        if (typeof field === 'object' && field !== null && 'text' in field) {
            return (field as { text: string }).text || ''
        }
        return typeof field === 'string' ? field : ''
    }

    const onSubmit = async (data: CreateTicketFormValues) => {
        const contentValue = data.ticket_content as unknown as EditorValue
        const contentText =
            typeof contentValue === 'object'
                ? contentValue.text
                : (contentValue as string) || ''

        const editorAttachments = Array.isArray(contentValue?.attachments)
            ? contentValue.attachments
            : []

        let uploadedAttachments: TicketAttachment[] = []
        if (editorAttachments.length > 0) {
            const files = editorAttachments.map((a) => a.file)
            const results = await uploadAsync(files)
            uploadedAttachments = results
                .filter((r: any) => r.status === 'fulfilled')
                .map((r: any) => r.value as TicketAttachment)
        }

        const dataToSubmit = {
            payload: {
                note: {
                    ...data,
                    // Strip downtime fields from ticket payload
                    include_downtime: undefined,
                    downtime_start: undefined,
                    downtime_end: undefined,
                    downtime_cause: undefined,
                    downtime_counts_against_sla: undefined,
                    service_startdate: dayjs(data.service_startdate).isValid()
                        ? dayjs(data.service_startdate).format('YYYY-MM-DD')
                        : undefined,
                    service_duedate: dayjs(data.service_duedate).isValid()
                        ? dayjs(data.service_duedate).format('YYYY-MM-DD')
                        : undefined,
                    service_suspend_until: data.service_suspend_until
                        ? dayjs(data.service_suspend_until).format('YYYY-MM-DD')
                        : undefined,
                    ticket_requested_at: data.ticket_requested_at
                        ? dayjs(data.ticket_requested_at).toISOString()
                        : undefined,
                    ticket_time_repply: data.ticket_time_repply
                        ? dayjs(data.ticket_time_repply).toISOString()
                        : undefined,
                    ticket_time_release: data.ticket_time_release
                        ? dayjs(data.ticket_time_release).toISOString()
                        : undefined,
                    ticket_estimated_completion_date:
                        data.ticket_estimated_completion_date
                            ? dayjs(
                                  data.ticket_estimated_completion_date
                              ).toISOString()
                            : undefined,
                    ticket_content: contentText,
                    ticket_root_cause: extractText(data?.ticket_root_cause),
                    ticket_remedial_measures: extractText(
                        data?.ticket_remedial_measures
                    ),
                    ticket_avoid: extractText(data?.ticket_avoid),
                    ticket_affected: extractText(data?.ticket_affected),
                    ticket_marjorable:
                        data?.ticket_type_id !== 2
                            ? false
                            : Boolean(data?.ticket_marjorable),
                },
                attachments: uploadedAttachments,
            },
        }

        try {
            await updateTicketMutation(dataToSubmit)

            // Handle downtime log create/update separately
            const isErrorService = data.ticket_type_group === 'error_service'
            const serviceId = Number(data.service_id)
            const ticketId = Number(data.ticket_id)

            if (
                isErrorService &&
                data.include_downtime &&
                data.downtime_start &&
                serviceId > 0
            ) {
                const downtimePayload = {
                    downtime_type: 'unplanned' as const,
                    downtime_start: dayjs(data.downtime_start).toISOString(),
                    downtime_end: data.downtime_end
                        ? dayjs(data.downtime_end).toISOString()
                        : undefined,
                    downtime_cause: (data.downtime_cause || 'provider') as
                        | 'provider'
                        | 'customer'
                        | 'third_party'
                        | 'force_majeure'
                        | 'scheduled',
                    downtime_description: extractText(data?.ticket_affected),
                    counts_against_sla: data.downtime_counts_against_sla,
                    ticket_id: ticketId,
                }

                try {
                    // Check if ticket already has a downtime log
                    const logsResponse = await getServiceDowntimeLogs(
                        serviceId,
                        {
                            page: 1,
                            page_size: 10,
                            q: '',
                        }
                    )
                    const existingLog = (logsResponse?.results || []).find(
                        (log: SLADowntimeLog) => log.ticket_id === ticketId
                    )

                    if (existingLog) {
                        await updateDowntimeLog(
                            serviceId,
                            existingLog.downtime_log_id,
                            downtimePayload
                        )
                    } else {
                        await createDowntimeLog(serviceId, downtimePayload)
                    }
                } catch (downtimeError) {
                    console.error('Error saving downtime log', downtimeError)
                    toast.error('Lưu downtime log thất bại')
                }
            }
        } catch (error) {
            console.error('Error submitting form', error)
        }
    }

    // Handle service change
    const handleServiceChange = (
        service:
            | Partial<
                  Service & { type: 'service'; label: string; value: number }
              >
            | { type: 'service_interesting'; label: string; value: string }
    ) => {
        setServiceSLA({})

        if (service.type === 'service') {
            setValue('service_id', service.service_id)
        } else {
            setValue('ticket_product_name', service.label)
            setValue('service_id', 0)
        }
    }

    // Handle SLA loaded from service
    const handleServiceSLALoaded = useCallback((slaData: ServiceSLAData) => {
        setServiceSLA(slaData)
        // Set SLA info for display (used to disable estimated completion field)
        if (slaData.resolutionTimeMinutes) {
            setSlaInfo({
                time: slaData.resolutionTimeMinutes,
                timeFormat: 'minute',
                source: 'service_sla',
            })
        }
    }, [])

    // Handle ticket type group change (disabled in edit mode)
    const handleChangeTicketTypeGroup = (_value: string) => {
        // Disabled in edit mode
    }

    // Handle ticket type change (disabled in edit mode)
    const handleChangeTicketType = (
        _ticketType: Partial<
            TicketTypeResponse & { value: number; label: string }
        >
    ) => {
        // Disabled in edit mode
    }

    const handleCancel = () => {
        if (onCancel) {
            onCancel()
        } else {
            navigate(`/tickets/${ticket?.ticket_id}`)
        }
    }

    return {
        formControl,
        control,
        handleSubmit,
        watch,
        setValue,
        isPending,
        slaInfo,
        onSubmit,
        handleServiceChange,
        handleServiceSLALoaded,
        handleChangeTicketTypeGroup,
        handleChangeTicketType,
        handleCancel,
        ticket,
    }
}
